import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpApi from 'i18next-http-backend';

/**
 * Last-resort label for a key that has no translation and no `defaultValue`.
 *
 * Previously a missing (or unparseable) bundle rendered the raw dotted key —
 * `dashboard.stats.activePlans` — straight into the UI, which both looks
 * broken and blows out layouts that were sized for real copy. Humanising the
 * leaf segment degrades to plausible English instead.
 *
 * Dev keeps the raw key so the gap is obvious while working.
 */
export function humanizeKey(key: string): string {
  const leaf = key.split('.').pop() ?? key;
  const words = leaf
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

i18n
  .use(HttpApi)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    supportedLngs: ['en', 'so'],
    fallbackLng: 'en',
    debug: import.meta.env.DEV,
    ns: ['translation'],
    defaultNS: 'translation',
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag', 'path', 'subdomain'],
      caches: ['localStorage'],
    },
    backend: {
      // Dev busts the browser cache on every fetch, so the hot reload below
      // actually gets the edited file. Prod appends a hash of the bundles'
      // contents (vite.config.ts): the URL changes exactly when a deploy
      // changes a translation, so a browser can never pair new code with a
      // cached bundle that lacks its keys, and otherwise keeps its cache.
      loadPath: import.meta.env.DEV
        ? () => `/locales/{{lng}}/{{ns}}.json?t=${Date.now()}`
        : `/locales/{{lng}}/{{ns}}.json?v=${import.meta.env.VITE_LOCALES_VERSION}`,
    },
    interpolation: {
      escapeValue: false,
    },
    // i18next routes `defaultValue` hits through here too — honour it first.
    parseMissingKeyHandler: (key, defaultValue) =>
      defaultValue ?? (import.meta.env.DEV ? key : humanizeKey(key)),
    react: {
      useSuspense: true,
    },
  });

// A bundle that 404s or fails to parse silently degrades every string in the
// app, so surface it loudly during development.
if (import.meta.env.DEV) {
  i18n.on('failedLoading', (lng, ns, msg) => {
    console.error(
      `[i18n] failed to load "${lng}/${ns}" — every key in this namespace will ` +
        `fall back. Check public/locales/${lng}/${ns}.json is valid JSON.`,
      msg
    );
  });
}

// Bundles live in `public/`, outside the module graph, so an edit never reached
// an open tab: newly added keys rendered as raw dotted names until a manual
// reload. The dev server announces bundle edits; refetch and re-render in place.
if (import.meta.hot) {
  import.meta.hot.on('locales:changed', async () => {
    await i18n.reloadResources();
    // useTranslation re-renders on `languageChanged`, not on a store reload.
    await i18n.changeLanguage(i18n.resolvedLanguage ?? i18n.language);
  });
}

export default i18n;
